import { useMemo, useState } from "react";
import { CalendarDays, TrendingUp, Clock, Sparkles, LineChart } from "lucide-react";
import { AnimatedCurrency } from "./AnimatedCurrency";
import { YtdMonthlyRevenueChartDialog } from "./YtdMonthlyRevenueChartDialog";
import {
  computeDashboardRevenue,
  computeMonthlyRevenueBreakdown,
  formatInvoiceAmount,
  formatServiceMonthLabel,
} from "../../utils/dashboardRevenue";

function InvoiceCountBadge({ count }) {
  const text = count === 1 ? "1 invoice" : `${count} invoices`;
  return (
    <span className="inline-flex items-center rounded-full border border-gray-200/80 bg-white/80 px-2.5 py-0.5 text-xs font-medium text-gray-600 dark:border-gray-600/60 dark:bg-gray-800/80 dark:text-gray-300">
      {text}
    </span>
  );
}

function MetricCard({
  title,
  amount,
  count,
  subtitle,
  breakdown,
  headerAction,
  icon: Icon,
  accent = "blue",
  loading,
  showHeaderAction = true,
}) {
  const accentStyles = {
    blue: "from-[#1a3268]/10 via-blue-500/5 to-transparent dark:from-blue-500/15",
    emerald: "from-emerald-500/10 via-emerald-400/5 to-transparent dark:from-emerald-500/15",
    violet: "from-violet-500/10 via-violet-400/5 to-transparent dark:from-violet-500/15",
  };

  const iconStyles = {
    blue: "bg-[#1a3268]/10 text-[#1a3268] dark:bg-blue-500/20 dark:text-blue-300",
    emerald: "bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
    violet: "bg-violet-500/10 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300",
  };

  return (
    <article className="group relative overflow-hidden rounded-2xl border border-gray-200/80 bg-white/90 p-6 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md dark:border-gray-700/80 dark:bg-gray-900/80 dark:hover:shadow-black/30">
      <div
        className={`pointer-events-none absolute inset-0 bg-gradient-to-br opacity-100 ${accentStyles[accent]}`}
      />
      <div className="relative flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium tracking-wide text-gray-500 dark:text-gray-400">
              {title}
            </p>
            {showHeaderAction && headerAction ? headerAction : null}
          </div>
          <p className="mt-2 text-3xl font-semibold tracking-tight text-gray-900 dark:text-gray-50">
            {loading ? (
              <span className="inline-block h-9 w-36 animate-pulse rounded-lg bg-gray-200/80 dark:bg-gray-700/80" />
            ) : (
              <AnimatedCurrency value={amount} />
            )}
          </p>
          {!loading && count != null ? (
            <div className="mt-2">
              <InvoiceCountBadge count={count} />
            </div>
          ) : null}
          {!loading && breakdown ? (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              <span className="font-medium text-gray-700 dark:text-gray-200">
                {formatInvoiceAmount(breakdown.recognized)}
              </span>
              {" recognized + "}
              <span className="font-medium text-violet-700 dark:text-violet-300">
                {formatInvoiceAmount(breakdown.draft)}
              </span>
              {" draft"}
              {breakdown.suffix ?? ""}
            </p>
          ) : null}
          {!loading && subtitle ? (
            <p className="mt-2 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
              {subtitle}
            </p>
          ) : null}
        </div>
        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${iconStyles[accent]}`}
        >
          <Icon className="h-5 w-5" aria-hidden />
        </div>
      </div>
    </article>
  );
}

function buildProjectedForecastSubtitle(metrics) {
  if (!metrics.isRunRateProjection || metrics.businessDaysTotal <= 0) {
    return null;
  }
  return `${formatInvoiceAmount(metrics.monthToDateRevenue)} to date · ${metrics.businessDaysPassed} of ${metrics.businessDaysTotal} business days · ~${formatInvoiceAmount(metrics.dailyAverage)}/day`;
}

function buildProjectedYtdSubtitle(metrics) {
  if (!metrics.isYtdMonthlyProjection) {
    return null;
  }
  if (metrics.ytdPreviousMonthCount > 0) {
    return `${formatInvoiceAmount(metrics.ytdPriorMonthsTotal)} from ${metrics.ytdPreviousMonthCount} prior mo · ~${formatInvoiceAmount(metrics.ytdMonthlyAverage)}/mo × 12`;
  }
  return "No prior months this year · projected annual revenue unavailable";
}

function DashboardRevenuePanel({
  invoices,
  revenueView,
  loading,
  initialLoading = false,
  clientCustgroup = null,
  scopeLabel = null,
  emptyMessage = null,
}) {
  const [ytdChartOpen, setYtdChartOpen] = useState(false);

  const metrics = useMemo(
    () => computeDashboardRevenue(invoices, revenueView, new Date(), clientCustgroup),
    [invoices, revenueView, clientCustgroup]
  );

  const monthlyBreakdown = useMemo(
    () => computeMonthlyRevenueBreakdown(invoices, new Date(), clientCustgroup),
    [invoices, clientCustgroup]
  );

  if (emptyMessage) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-300/80 bg-white/50 px-6 py-12 text-center dark:border-gray-600/80 dark:bg-gray-900/40">
        <p className="text-sm text-gray-500 dark:text-gray-400">{emptyMessage}</p>
      </div>
    );
  }

  const projectedForecastSubtitle = buildProjectedForecastSubtitle(metrics);
  const projectedYtdSubtitle = buildProjectedYtdSubtitle(metrics);

  const showYtdChart = revenueView === "current";

  return (
    <>
      <YtdMonthlyRevenueChartDialog
        open={ytdChartOpen}
        onOpenChange={setYtdChartOpen}
        monthlyData={monthlyBreakdown}
        year={metrics.currentYear}
        scopeLabel={scopeLabel}
      />
      <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-gray-200/80 bg-white/70 px-4 py-3 text-sm text-gray-600 shadow-sm backdrop-blur-sm dark:border-gray-700/80 dark:bg-gray-900/50 dark:text-gray-300">
        <CalendarDays className="h-4 w-4 shrink-0 text-[#1a3268] dark:text-blue-300" />
        <span>
          Calendar year{" "}
          <strong className="font-medium text-gray-900 dark:text-gray-100">
            {metrics.currentYear}
          </strong>
        </span>
        <span className="hidden text-gray-300 sm:inline dark:text-gray-600">|</span>
        <span>
          Current service month:{" "}
          <strong className="font-medium text-gray-900 dark:text-gray-100">
            {formatServiceMonthLabel(metrics.currentMonthKey)}
          </strong>
        </span>
        <span className="hidden text-gray-300 md:inline dark:text-gray-600">|</span>
        <span className="hidden md:inline">
          Previous:{" "}
          <strong className="font-medium text-gray-900 dark:text-gray-100">
            {formatServiceMonthLabel(metrics.previousMonthKey)}
          </strong>
        </span>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
        <MetricCard
          title="Year to Date Revenue"
          amount={metrics.yearToDate}
          count={metrics.yearToDateCount}
          headerAction={
            showYtdChart ? (
              <button
                type="button"
                className="app-btn-gloss shrink-0 gap-1 px-2 py-1 text-xs"
                onClick={() => setYtdChartOpen(true)}
                title="View monthly revenue chart"
                aria-label="View monthly revenue chart"
              >
                <LineChart className="h-3.5 w-3.5 shrink-0" />
                Chart
              </button>
            ) : null
          }
          showHeaderAction={!initialLoading}
          subtitle={
            revenueView === "projected"
              ? projectedYtdSubtitle
              : `Jan–${formatServiceMonthLabel(metrics.currentMonthKey)} · non-draft only`
          }
          icon={TrendingUp}
          accent="blue"
          loading={loading}
        />
        <MetricCard
          title="Current Month Forecast"
          amount={metrics.currentMonthForecast}
          count={metrics.currentMonthForecastCount}
          breakdown={
            metrics.currentMonthDraft > 0
              ? {
                  recognized: metrics.currentMonthRecognized,
                  draft: metrics.currentMonthDraft,
                  suffix: revenueView === "projected" ? " to date" : "",
                }
              : null
          }
          subtitle={
            revenueView === "projected"
              ? projectedForecastSubtitle
              : `${formatServiceMonthLabel(metrics.currentMonthKey)} · recognized + draft`
          }
          icon={Sparkles}
          accent="violet"
          loading={loading}
        />
        <MetricCard
          title="Previous Month's Revenue"
          amount={metrics.previousMonthRevenue}
          count={metrics.previousMonthCount}
          subtitle={`${formatServiceMonthLabel(metrics.previousMonthKey)} · non-draft only`}
          icon={Clock}
          accent="emerald"
          loading={loading}
        />
      </div>
    </>
  );
}

export { DashboardRevenuePanel };
